import React, {useState, useEffect} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {Modal, Button, Headline} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import useUpdateBrandProfileNote from '@/hooks/mutations/use-update-brand-profile-note';
import useBrandProfileNoteQuery from '@/hooks/queries/use-brand-profile-note-query';
import useBrandId from '@/hooks/use-brand-id';
import styles from './AddNoteModal.module.scss';

interface AddNoteModalProps {
  open: boolean;
  onClose: () => void;
  creatorId: string | null;
  creatorName?: string;
}

function AddNoteModal({
  open,
  onClose,
  creatorId,
  creatorName,
}: AddNoteModalProps) {
  const brandId = useBrandId();
  const [text, setText] = useState('');

  const {data: note, isLoading: isLoadingNote} = useBrandProfileNoteQuery({
    brandId,
    profileId: creatorId ?? undefined,
    enabled: open && !!creatorId,
  });

  const updateNote = useUpdateBrandProfileNote({
    brandId,
    profileId: creatorId ?? '',
  });

  useEffect(() => {
    if (open && note) {
      setText(note.bodyRaw);
    }
    if (!open) {
      setText('');
    }
  }, [open, note]);

  const handleSave = async () => {
    if (!note || note.bodyRaw === text) {
      onClose();
      return;
    }

    try {
      await updateNote.mutateAsync({...note, bodyRaw: text});
      onClose();
    } catch {
      // mutation error is handled by React Query's global error handler
    }
  };

  return (
    <Modal open={open} handleClose={onClose} showCloseButton size="large">
      <div className={styles.modalContent}>
        <Headline size="sm">
          {creatorName
            ? `${translate('Add note')} - ${creatorName}`
            : translate('Add note')}
        </Headline>
        <TextareaAutosize
          className={styles.textarea}
          minRows={8}
          maxRows={16}
          placeholder={translate('Write a note about this creator...')}
          value={text}
          onChange={({target: {value}}) => setText(value)}
          disabled={updateNote.isLoading}
        />
        <div className={styles.buttonRow}>
          <Button variant="text" onClick={onClose}>
            {translate('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isLoadingNote || updateNote.isLoading}
          >
            {updateNote.isLoading ? translate('Saving...') : translate('Save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default AddNoteModal;
